/**
 * T63 Part J — canonical Mastery Workflow objective catalogue.
 *
 * The backend `mastery_workflows.objective` column is `varchar(191)`
 * (nullable, free-text — see MasteryWorkflowController::save line
 * 391: `'objective' => 'nullable|string|max:191'`), so the enum lives
 * on the frontend. The stored value is a human-readable label
 * ("Increase Percent Correct"), NOT a canonical key — historical rows
 * already have those labels on disk, so we preserve them.
 *
 * The `key` field below is a stable slug used only by the query
 * string on /workflows/new; the `label` field is what gets persisted
 * and rendered. `labelFromKey()` resolves a key from the URL back to
 * the persisted label. Legacy stored labels (e.g. hand-typed
 * objectives from before this catalogue) still resolve to their own
 * text on the editor's OBJECTIVE header via the identity fallback.
 *
 * Criterion-side implications:
 *   Percent Correct family is the ONLY objective whose criteria
 *   fields (minPercentCorrect / acrossSessions / minTrialsPerSession)
 *   are backed by real columns on mastery_workflow_phases today.
 *   Frequency / Rate / Duration / Manual are exposed on the chooser
 *   so the flow can be corrected end-to-end, but their per-phase
 *   criterion fields fall back to a Percent-Correct-shaped card until
 *   the backend adds their columns. That gap is documented explicitly
 *   in the chooser UI (`fullyImplemented=false`) and in the final
 *   report — no dead selectable options, but a clear "criteria coming
 *   soon" hint on unsupported families.
 */

export type ObjectiveCategory =
  | "percent_correct"
  | "frequency"
  | "rate"
  | "duration"
  | "manual";

export type ObjectiveDirection = "increase" | "decrease" | "manual";

export interface WorkflowObjective {
  /** Stable slug used in URL query strings. */
  key: string;
  /** Category grouping shown in the chooser. */
  category: ObjectiveCategory;
  /** Human-readable value stored in mastery_workflows.objective. */
  label: string;
  /** Direction — informs mastery comparison operator (>=, <=, N/A). */
  direction: ObjectiveDirection;
  /** True when per-phase criterion fields are fully backed by schema
   *  AND the evaluator has a branch for this family. */
  fullyImplemented: boolean;
  /** Chip label to show in the chooser when disabled. Falls back to
   *  "Not available yet" when unset. Used to communicate a SPECIFIC
   *  reason (e.g. "Requires observation-duration support"). */
  disabledReason?: string;
}

/** Family classifier — mirrors backend deriveCriterionMetadata dispatch.
 *  Frontend uses this to render the correct criterion UI in PhaseCard. */
export type ObjectiveFamily = "percent_correct" | "frequency" | "rate" | "duration" | "manual";

export function objectiveFamily(key: string | null | undefined): ObjectiveFamily {
  const k = (key ?? "").toLowerCase();
  if (k === "increase_frequency" || k === "reduce_frequency") return "frequency";
  if (k === "increase_rate_per_minute" || k === "reduce_rate_per_minute"
      || k === "increase_rate_per_hour" || k === "reduce_rate_per_hour") return "rate";
  if (k.endsWith("_observed_duration")) return "duration";
  if (k === "manual_mastery") return "manual";
  return "percent_correct";
}

/** Duration aggregation slice inferred from key. */
export function durationAggregation(key: string | null | undefined):
  "total" | "avg" | "min" | "max" | null {
  const k = (key ?? "").toLowerCase();
  if (k.includes("total_observed")) return "total";
  if (k.includes("avg_observed"))   return "avg";
  if (k.includes("min_observed"))   return "min";
  if (k.includes("max_observed"))   return "max";
  return null;
}

/** Direction (increase vs reduce) inferred from key. */
export function isReduceObjective(key: string | null | undefined): boolean {
  const k = (key ?? "").toLowerCase();
  return k.startsWith("reduce_") || k === "reduce_percentage";
}

export const WORKFLOW_OBJECTIVES: WorkflowObjective[] = [
  // Percent Correct — the only fully-implemented family today.
  { key: "increase_percent_correct", category: "percent_correct",
    label: "Increase Percent Correct", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_percentage", category: "percent_correct",
    label: "Reduce Percentage", direction: "decrease",
    fullyImplemented: true },
  // Frequency
  // T63 Part O — Frequency family enabled end-to-end. Backend derives
  // metricType/comparisonOperator from these keys; the phase editor
  // renders "Min./Max. Frequency to master" instead of Percent Correct.
  { key: "increase_frequency", category: "frequency",
    label: "Increase Frequency", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_frequency", category: "frequency",
    label: "Reduce Frequency", direction: "decrease",
    fullyImplemented: true },
  // Rate per HOUR — enabled once session_data_records grew the
  // observationDurationSeconds column (migration 2026_09_02_100000).
  // Sessions without a measured observation window do NOT qualify for
  // Rate/hour evaluation; the evaluator skips them rather than falling
  // back to event-span (which would misrepresent 10 minutes of
  // observation as 10 minutes of event span).
  { key: "increase_rate_per_hour", category: "rate",
    label: "Increase Rate per Hour", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_rate_per_hour", category: "rate",
    label: "Reduce Rate per Hour", direction: "decrease",
    fullyImplemented: true },
  // Rate per MINUTE — canonical value emitted by
  // SessionDataAggregationService::summarizeFrequency (ratePerMinute).
  { key: "increase_rate_per_minute", category: "rate",
    label: "Increase Rate per Minute", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_rate_per_minute", category: "rate",
    label: "Reduce Rate per Minute", direction: "decrease",
    fullyImplemented: true },
  // Duration (canonical unit = seconds; canonical source =
  // SessionDataAggregationService::summarizeDuration).
  { key: "increase_total_observed_duration", category: "duration",
    label: "Increase Total Observed Duration", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_total_observed_duration", category: "duration",
    label: "Reduce Total Observed Duration", direction: "decrease",
    fullyImplemented: true },
  { key: "increase_min_observed_duration", category: "duration",
    label: "Increase Min. Observed Duration", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_min_observed_duration", category: "duration",
    label: "Reduce Min. Observed Duration", direction: "decrease",
    fullyImplemented: true },
  { key: "increase_max_observed_duration", category: "duration",
    label: "Increase Max. Observed Duration", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_max_observed_duration", category: "duration",
    label: "Reduce Max. Observed Duration", direction: "decrease",
    fullyImplemented: true },
  { key: "increase_avg_observed_duration", category: "duration",
    label: "Increase Avg. Observed Duration", direction: "increase",
    fullyImplemented: true },
  { key: "reduce_avg_observed_duration", category: "duration",
    label: "Reduce Avg. Observed Duration", direction: "decrease",
    fullyImplemented: true },
  // Manual Mastery — selectable; evaluator NEVER auto-advances.
  // Manual-advance endpoint deferred to a follow-up ticket; the phase
  // editor renders explanatory text instead of a threshold field.
  { key: "manual_mastery", category: "manual",
    label: "Manual Mastery", direction: "manual",
    fullyImplemented: true },
];

export const CATEGORY_LABEL: Record<ObjectiveCategory, string> = {
  percent_correct: "Percent Correct",
  frequency:       "Frequency",
  rate:            "Rate",
  duration:        "Duration",
  manual:          "Manual",
};

/** Look up an objective by URL-query key. */
export function objectiveByKey(key: string | null | undefined): WorkflowObjective | null {
  if (!key) return null;
  return WORKFLOW_OBJECTIVES.find((o) => o.key === key) ?? null;
}

/** Map a persisted label back to a catalogue entry — historical rows
 *  that predate this catalogue keep working via label match. */
export function objectiveByLabel(label: string | null | undefined): WorkflowObjective | null {
  if (!label) return null;
  const norm = label.trim().toLowerCase();
  return WORKFLOW_OBJECTIVES.find((o) => o.label.toLowerCase() === norm) ?? null;
}

export function groupedObjectives(): Array<{ category: ObjectiveCategory; items: WorkflowObjective[] }> {
  const order: ObjectiveCategory[] = ["percent_correct", "frequency", "rate", "duration", "manual"];
  return order.map((category) => ({
    category,
    items: WORKFLOW_OBJECTIVES.filter((o) => o.category === category),
  }));
}
